import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { extractDomain } from "@/utils/emailUtils";
import { Headset, ChevronDown, ChevronRight, PlusCircle } from 'lucide-react';
import { Separator } from "@/components/ui/separator";

type Ticket = {
  id: string;
  properties: {
    subject?: string;
    createdate?: string;
    [key: string]: any;
  };
  stage_name: string;
  created_at?: string;
  updated_at?: string;
};

const TicketsSection: React.FC = () => {
  const [showTickets, setShowTickets] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.email) {
      fetchTicketsCount();
      
      if (showTickets) {
        fetchTickets();
      }
    }
  }, [user, showTickets]);

  const fetchTicketsCount = async () => {
    if (!user?.email) return;
    
    try {
      const domain = extractDomain(user.email);
      
      const { count, error } = await supabase
        .from('revenuehub_tickets')
        .select('*', { count: 'exact', head: true })
        .eq('empresa', domain)
        .not('stage_name', 'eq', 'Tecnicamente Inviable - 0%')
        .not('stage_name', 'eq', 'Resuelto - 100%')
        .not('stage_name', 'eq', 'Detenido/Pausado - 0%')
        .not('stage_name', 'eq', 'Backlog (Despriorizadas)');
      
      if (error) throw error;
      
      setActiveCount(count || 0);
    } catch (error) {
      console.error('Error fetching tickets count:', error);
    }
  };

  const fetchTickets = async () => {
    if (!user?.email) return;
    
    setIsLoading(true);
    const domain = extractDomain(user.email);
    
    try {
      const { data, error } = await supabase
        .from('revenuehub_tickets')
        .select('id, properties, stage_name, created_at, updated_at')
        .eq('empresa', domain)
        .not('stage_name', 'eq', 'Tecnicamente Inviable - 0%')
        .not('stage_name', 'eq', 'Resuelto - 100%')
        .not('stage_name', 'eq', 'Detenido/Pausado - 0%')
        .not('stage_name', 'eq', 'Backlog (Despriorizadas)')
        .order('updated_at', { ascending: false })
        .limit(3);
      
      if (error) {
        console.error('Error fetching tickets:', error);
        return;
      }
      
      const typedData: Ticket[] = data.map(item => ({
        id: item.id,
        properties: typeof item.properties === 'object' ? item.properties : {},
        stage_name: item.stage_name,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));
      
      setTickets(typedData);
    } catch (error) {
      console.error('Error in tickets fetch operation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTickets = () => {
    setShowTickets(!showTickets);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return `${date.getDate()} ${date.toLocaleString('es', { month: 'short' })}`;
  };

  const handleCreateTicket = () => {
    navigate('/support');
  };

  const handleViewAllTickets = () => {
    navigate('/tickets');
  };

  if (!user) return null;

  return (
    <div className="mt-2 px-2">
      <button 
        onClick={handleToggleTickets}
        className="w-full flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-gray-700 hover:bg-blue-100"
      >
        <div className="flex items-center">
          <Headset className="w-5 h-5 mr-2 text-blue-600" />
          <span className="font-semibold">Tickets: </span>
          <span className="ml-1 font-bold">{activeCount} activos</span>
        </div>
        {showTickets ? 
          <ChevronDown className="w-4 h-4 text-gray-500" /> : 
          <ChevronRight className="w-4 h-4 text-gray-500" />
        }
      </button>

      {showTickets && (
        <div className="mt-2 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-3 border-b border-gray-100 bg-gray-50">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium text-gray-700">Mis tickets de soporte</h3>
              <button 
                className="text-blue-500 text-xs hover:underline"
                onClick={handleViewAllTickets}
              >
                Ver todos
              </button>
            </div>
          </div>
          
          <div className="divide-y divide-gray-100">
            {isLoading ? (
              <div className="p-3 text-center text-sm text-gray-500">
                Cargando tickets...
              </div>
            ) : tickets.length > 0 ? (
              tickets.map(ticket => (
                <div key={ticket.id} className="p-3">
                  <div className="flex justify-between">
                    <div className="text-xs font-medium text-gray-500 truncate max-w-[140px]">
                      {ticket.stage_name}
                    </div>
                    <span className="text-xs text-gray-400">{formatDate(ticket.updated_at)}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5 line-clamp-2">
                    {ticket.properties?.subject || "Sin asunto"}
                  </p>
                </div>
              ))
            ) : (
              <div className="p-3 text-center text-sm text-gray-500">
                No hay tickets activos
              </div>
            )}
          </div>
          
          <div className="p-2 bg-gray-50 border-t border-gray-100">
            <button
              onClick={handleCreateTicket}
              className="w-full text-blue-500 text-sm py-1 flex items-center justify-center hover:bg-blue-50 rounded"
            >
              <PlusCircle className="w-4 h-4 mr-1" />
              <span>Nuevo ticket</span>
            </button>
          </div>
        </div>
      )}
      
      <Separator className="my-2" />
    </div>
  );
};

export default TicketsSection;
